import axios from "axios";
import { Preferences } from "@capacitor/preferences";

// ─────────────────────────────────────────────────────────────────────────────
// Keeping the hiring session alive.
//
// The hiring module is the only module that authenticates per request with the
// signed session token; every other screen passes ids in the request body. That
// asymmetry is what made the original bug so confusing: the token aged out at
// JWT_EXPIRES_IN (a day), the rest of the app carried on working, and only /ats
// broke — with "Failed to load jobs. Please try again.", which reads like a
// server fault and could never come true, because the only cure was signing in
// again.
//
// So the token is now renewed under the app instead of being left to expire.
// Two clocks, set by the server: JWT_EXPIRES_IN is how long one token is good
// for, JWT_REFRESH_WINDOW_DAYS is how long the SESSION lasts — how far back the
// server will still trade an old token for a new one. Somebody who opens
// hiring within the window never sees the OTP screen; a token that leaks still
// stops working inside the day.
// ─────────────────────────────────────────────────────────────────────────────

// The one place this key is spelled in this module. AutoTaskContext writes the
// same key at login and clears it at logout (STORAGE_KEYS.TOKEN) — a typo in
// either place does not throw, it just looks like the session randomly drops.
export const TOKEN_KEY = "token";

const REFRESH_URL = `${process.env.REACT_APP_API_BASE_URL}/api/auth/refresh-session`;

// Renew this long before the token's own expiry rather than at it. A request
// that leaves with 20 seconds on the clock can still land after it — server
// clocks are not our clock — and that request would 401 for no good reason.
const RENEW_BEFORE_MS = 5 * 60 * 1000;

// Where the "you were signed out" line is left for the login screen to pick up,
// so the redirect below does not dump somebody on a login form with no idea why
// they are looking at it.
export const SESSION_NOTICE_KEY = "hiring.sessionNotice";

export const readToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private mode, blocked storage. No token means the request goes out
    // unauthenticated and the server says so, which is the safe direction.
    return null;
  }
};

// Both homes, every time.
//
// Preferences is the durable copy the app boots from — AutoTaskContext mirrors
// it into localStorage on load — and localStorage is what the interceptor reads
// on each request. Write only localStorage and the session keeps itself alive
// beautifully until the next page reload, which then restores the stale token
// from Preferences and puts the bug straight back.
const writeToken = async (token) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* see readToken */
  }
  try {
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } catch {
    /* see readToken */
  }
};

const forgetToken = async () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* see readToken */
  }
  try {
    await Preferences.remove({ key: TOKEN_KEY });
  } catch {
    /* see readToken */
  }
};

// The token's `exp`, in milliseconds, or null when it cannot be read.
//
// This does NOT verify anything and must never be used to decide what the user
// may do — only the server's verify does that. It answers one question: is it
// worth asking for a new token before sending this request? A tampered `exp`
// buys an attacker nothing but an early refresh call they would have got anyway.
//
// null means "unreadable", and every caller treats that as "just send it and
// let the server judge" rather than as "expired". A shape we do not recognise
// must not lock somebody out of a module that would have worked.
const expiryCache = new Map();

const expiryOf = (token) => {
  if (expiryCache.has(token)) return expiryCache.get(token);

  let exp = null;
  try {
    const payload = token.split(".")[1];
    if (payload) {
      // base64url → base64, and pad: atob rejects the unpadded, URL-safe
      // alphabet that JWTs actually use.
      const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        "=",
      );
      const claims = JSON.parse(atob(padded));
      if (typeof claims.exp === "number") exp = claims.exp * 1000;
    }
  } catch {
    exp = null;
  }

  // Bounded so a long-lived tab that sees many tokens cannot grow this without
  // limit. Tokens arrive one at a time; a handful of entries is the whole
  // working set.
  if (expiryCache.size > 8) expiryCache.clear();
  expiryCache.set(token, exp);
  return exp;
};

// One refresh at a time.
//
// A dashboard fires several requests in the same tick. Without this they would
// each notice the same stale token and each start their own refresh, and the
// last one to land would win — which is fine — after having spent four round
// trips and minted four tokens to get there. Worse, a slow one could finish
// after a newer one and write the OLDER token back over it.
let inFlight = null;

export const refreshSession = () => {
  if (inFlight) return inFlight;

  const presented = readToken();
  if (!presented) return Promise.reject(new Error("No session token to renew"));

  inFlight = axios
    .post(
      REFRESH_URL,
      // Sent both ways because the server accepts either, and a bare axios call
      // carries none of the hiring client's interceptors — deliberately, since
      // this request must not recurse into the 401 handling that calls it.
      { token: presented },
      { headers: { Authorization: `Bearer ${presented}` } },
    )
    .then(async ({ data }) => {
      const next = data?.data?.token;
      if (!next) throw new Error("Session refresh returned no token");
      await writeToken(next);
      return next;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

// Make sure the token about to be attached will still be valid when the request
// lands. Returns whatever should go on the header — possibly the old token.
export const ensureFreshToken = async () => {
  const token = readToken();
  if (!token) return null;

  const exp = expiryOf(token);
  if (exp === null) return token;
  if (exp - Date.now() > RENEW_BEFORE_MS) return token;

  try {
    return await refreshSession();
  } catch {
    // Past the refresh window, or the account is no longer active. Send the old
    // token anyway: the server is the one that gets to say the session is over,
    // and the response interceptor turns its 401 into a sign-in. Deciding it
    // here would sign people out on a refresh call that merely timed out.
    return readToken();
  }
};

// The end of the road: the server has refused to renew. Only reachable when the
// session is genuinely finished — idle past JWT_REFRESH_WINDOW_DAYS, or the
// account deactivated — so this is the one case where signing in again is the
// honest answer rather than a papered-over bug.
let ending = false;

export const endSession = async (
  message = "Your session has expired. Please sign in again.",
) => {
  // Several requests can 401 together. Without this they would each clear
  // storage and each call replace(), and the login screen would be torn down
  // and rebuilt under the person reading it.
  if (ending) return;
  ending = true;

  await forgetToken();

  try {
    sessionStorage.setItem(SESSION_NOTICE_KEY, message);
  } catch {
    /* the notice is a courtesy; losing it must not block the redirect */
  }

  // replace, not assign: the expired page must not be a back target, or the
  // back button lands on a screen that will only 401 again.
  window.location.replace("/login");
};
